import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Filter,
  FilterConfigDelegate,
  GridIndexPath,
  IndexPath,
  ItemSelectionStore,
  SnackbarService,
  UniversalFilterStore,
} from '@shared/ui';
import {
  FilterConfigStore,
  RoiStore,
  RoiChangeStore,
  MosaicGalleryPanelDelegate,
  MosaicGalleryPanels,
} from '../../stores';
import { ProtocolStore } from '@telespot/protocols/ui';
import { Option } from '@telespot/protocols/data-access';
import { FilterCriterion } from '@analysis/domain';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import {
  FilterConfigEffects,
  MosaicRoiFeatureEffects,
  RoiLabelingEffects,
  RoiRevertEffects,
  FilterAutocompleteEffects,
  RoiFilteringEffects,
} from '../../effects';
import { MosaicCellData } from '../mosaic-cell';
import { MosaicGalleryRowNumberData } from '../mosaic-row-number';
import { MosaicGalleryHeaderData } from '../mosaic-section-header';
import { FilterSetEditorComponent } from '../filter-set-editor';

/**
 * TODO:
 * - split into features
 * - test
 */
@Injectable()
export class MosaicGalleryFacade {
  private readonly _dragSelectionEnabled$ = new BehaviorSubject<boolean>(false);
  private readonly _editingEnabled$ = new BehaviorSubject<boolean>(false);

  public readonly dragSelectionEnabled$ = this._dragSelectionEnabled$.asObservable();

  public readonly draggingDisabled$ = this.itemSelectionStore.select(
    this.itemSelectionStore.multiselectionEnabled$,
    this._dragSelectionEnabled$,
    this._editingEnabled$,
    this.roiChangeStore.applyingChanges$,
    this.roiChangeStore.revertingChanges$,
    (multi, drag, editing, applyingChanges, revertingChanges) =>
      multi || drag || !editing || applyingChanges || revertingChanges
  );

  public readonly searchFields = this.filterAutocompleteEffects.searchFields;

  public readonly selectedItems$ = this.itemSelectionStore.selectedItems$;
  public readonly filters$ = this.universalFilterStore.filters$;
  public readonly selectedConfig$ = this.filterConfigStore.selectedConfig$;
  public readonly configs$ = this.filterConfigStore.configs$;
  public readonly filteredLabelIndex$ = this.roiFilteringEffects.filteredLabelIndex$;
  public readonly sectionCellCounts$ = this.roiFilteringEffects.sectionCellCounts$;
  public readonly visibleSections$ = this.roiFilteringEffects.visibleSections$;
  public readonly selectedLabelCounts$ = this.roiFilteringEffects.selectedLabelCounts$;
  public readonly contextMenu$ = this.roiFilteringEffects.contextMenu$;

  public readonly addLabelsToFocusedItems = this.roiStore.effect((indexPath$: Observable<IndexPath>) =>
    indexPath$.pipe(
      concatLatestFrom(() => [
        this.filteredLabelIndex$,
        this.protocolStore.options$,
        this.universalFilterStore.filters$,
      ]),
      map(([indexPath, labelIndex, labels, filters]) => {
        const visibleSections = Array.from(labelIndex.keys());
        const destinationId = visibleSections[indexPath.section];
        const destinationLabel = labels[destinationId];

        const activeLabelFilters = filters
          .filter((filter) => filter.field.id === 'label')
          .map((filter) => Object.values(labels).find((label) => label.name === filter.value)?.id);

        const resultLabels = destinationLabel ? [destinationLabel.id] : activeLabelFilters;

        this.panelRegistry.addLabelsToFocusedItems(resultLabels);
      })
    )
  );

  public readonly addLabels = this.roiStore.effect((labels$: Observable<string[]>) =>
    labels$.pipe(
      concatLatestFrom(() => this.itemSelectionStore.selectedItemsArray$),
      filter(([, selectedItems]) => selectedItems.length > 0),
      tap(([labels, selectedItems]) => {
        if (labels.length === 0) this.roiLabelingEffects.removeAllLabels(selectedItems);
        else this.roiLabelingEffects.addLabels({ roiIds: selectedItems, labels });

        this.itemSelectionStore.clearSelectedItems();
      })
    )
  );

  public readonly selectRoisByIndexPaths = this.roiFilteringEffects.selectRoisByIndexPaths;

  public readonly loadMore = this.roiStore.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      concatLatestFrom(() => [this.universalFilterStore.filters$, this.protocolStore.options$]),
      filter(([, filters]) => !this.isLoadMoreGated(filters)),
      tap(([sampleId, filters, options]) => {
        const labels = this.resolveLabelIds(filters, options ?? {});
        this.mosaicRoiFeatureEffects.loadMore({ sampleId, labels });
      })
    )
  );

  constructor(
    private readonly protocolStore: ProtocolStore,
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly panelRegistry: MosaicGalleryPanels,
    private readonly mosaicRoiFeatureEffects: MosaicRoiFeatureEffects,
    private readonly universalFilterStore: UniversalFilterStore,
    private readonly filterConfigStore: FilterConfigStore,
    private readonly filterConfigEffects: FilterConfigEffects,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly roiRevertEffects: RoiRevertEffects,
    private readonly itemSelectionStore: ItemSelectionStore,
    private readonly filterAutocompleteEffects: FilterAutocompleteEffects,
    private readonly roiFilteringEffects: RoiFilteringEffects,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService
  ) {}

  public applyFilterConfig(configId: string, criteria: FilterCriterion[]): void {
    // Rebuild the full UI Filter[] from the live field registry, restoring each field's
    // SearchField (incl. its autocomplete/format callbacks). Criteria whose field is no
    // longer present in the registry are skipped.
    const fieldMap = this.filterAutocompleteEffects.fieldMap;
    const filters: Filter[] = criteria
      .map((criterion) => {
        const field = fieldMap[criterion.fieldId];
        return field ? { field, operator: criterion.operator, value: criterion.value } : undefined;
      })
      .filter((filter): filter is Filter => filter !== undefined);

    this.filterConfigStore.selectConfig(configId);
    this.universalFilterStore.setFilters(filters);
  }

  public upsertSearchFilter(filter: Filter): void {
    this.universalFilterStore.upsertFilter(filter);
  }

  public removeFilter(f: Filter): void {
    this.universalFilterStore.removeFilter(f);
  }

  public clearAllFilters(): void {
    this.universalFilterStore.clearFilters();
  }

  public filterByLabel(labelName: string): void {
    this.roiFilteringEffects.filterByLabel(labelName);
  }

  public filterByUnlabeled(): void {
    this.roiFilteringEffects.filterByUnlabeled();
  }

  public filterByAsset(assetId: string): void {
    this.roiFilteringEffects.filterByAsset(assetId);
  }

  public filterByChanged(): void {
    this.roiFilteringEffects.filterByChanged();
  }

  public filterBySelected(): void {
    this.roiFilteringEffects.filterBySelected();
  }

  public deleteFilterConfig(configId: string): void {
    this.filterConfigEffects.deleteConfig(configId);
    this.snackbarService.show('filter.configuration_deleted');
  }

  public removeLabel(label: string) {
    this.itemSelectionStore.selectedItems$.pipe(take(1)).subscribe((selectedItems) => {
      if (selectedItems.size === 0) return;

      const roiIds = Array.from(selectedItems);

      this.roiLabelingEffects.removeLabels({ roiIds, labels: [label] });
    });
  }

  public saveFilterConfig(name: string, filters: Filter[]): void {
    this.filterConfigEffects.saveConfig({ filters, name });
    this.snackbarService.show('filter.configuration_saved');
  }

  public createFilterConfig(delegate: FilterConfigDelegate, filters: Filter[]): void {
    this.dialog.open(FilterSetEditorComponent, {
      hasBackdrop: true,
      disableClose: true,
      panelClass: ['rounded'],
      data: { delegate, filters },
    });
  }

  // #region GridViewDataSource

  public numberOfSections(): Observable<number> {
    return this.sectionCellCounts$.pipe(map((counts) => counts.length));
  }

  public numberOfCellsInSection(section: number): Observable<number> {
    return this.sectionCellCounts$.pipe(map((counts) => counts[section] ?? 0));
  }

  public getCellForIndexPath(
    indexPath: GridIndexPath,
    cellsPerRow: number,
    hoverLabel: string | undefined
  ): Observable<MosaicCellData> {
    return this.roiFilteringEffects.getCellForIndexPath(indexPath, cellsPerRow, hoverLabel);
  }

  public getRowNumberForIndexPath(indexPath: IndexPath, cellsPerRow: number): Observable<MosaicGalleryRowNumberData> {
    return this.roiFilteringEffects.getRowNumberForIndexPath(indexPath, cellsPerRow);
  }

  public getHeaderForIndexPath(index: number): Observable<MosaicGalleryHeaderData | undefined> {
    return this.roiFilteringEffects.getHeaderForIndexPath(index);
  }

  // #region ItemSelection delegation

  public clearSelection(): void {
    this.itemSelectionStore.clearSelectedItems();
  }

  public setMultiselectionEnabled(enabled: boolean): void {
    this.itemSelectionStore.setMultiselectionEnabled(enabled);
  }

  public setDragSelectionEnabled(enabled: boolean): void {
    this._dragSelectionEnabled$.next(enabled);
  }

  public get editingEnabled(): boolean {
    return this._editingEnabled$.value;
  }

  public setEditingEnabled(enabled: boolean): void {
    this._editingEnabled$.next(enabled);
  }

  public setRoiBoxVisible(visible: boolean): void {
    this.roiFilteringEffects.setRoiBoxVisible(visible);
  }

  public selectItem(roiId: string): void {
    this.itemSelectionStore.selectItem(roiId);
  }

  public toggleItem(roiId: string): void {
    this.itemSelectionStore.toggleItem(roiId);
  }

  public addSelectedItem(roiId: string): void {
    this.itemSelectionStore.addSelectedItem(roiId);
  }

  public removeSelectedItem(roiId: string): void {
    this.itemSelectionStore.removeSelectedItem(roiId);
  }

  public addSelectedItems(roiIds: string[]): void {
    this.itemSelectionStore.addSelectedItems(roiIds);
  }

  // #endregion

  // #region Panel delegation

  public focusPanel(panelId: number | Observable<number>): void {
    this.panelRegistry.focusPanel(panelId);
  }

  public setPanelRef(panelId: number, ref: MosaicGalleryPanelDelegate): void {
    this.panelRegistry.setPanelRef({ panelId, ref });
  }

  public unregisterPanelRef(panelId: number, expectedRef: unknown): void {
    this.panelRegistry.unregisterPanelRef({ panelId, expectedRef });
  }

  // #endregion

  // #region MosaicRoiFeature delegation

  public loadBlob(roiId: string): void {
    this.mosaicRoiFeatureEffects.loadBlob(roiId);
  }

  public cancelLoadBlob(roiId: string): void {
    this.mosaicRoiFeatureEffects.cancelLoadBlob(roiId);
  }

  public loadMoreRoiLabels(roiId: string): void {
    this.mosaicRoiFeatureEffects.loadMoreRoiLabels(roiId);
  }

  public cancelLoadMoreRoiLabels(roiId: string): void {
    this.mosaicRoiFeatureEffects.cancelLoadMoreRoiLabels(roiId);
  }

  // #endregion

  // #region Revert delegation

  public revertSelectedRoiChanges(): void {
    this.roiRevertEffects.revertSelectedRoiChanges();
  }

  // #endregion

  // #region Labeling delegation

  public removeAllLabels(roiIds: string[] | Observable<string[]>): void {
    this.roiLabelingEffects.removeAllLabels(roiIds);
  }

  // #endregion

  public selectRois(roiIds: string[]): void {
    this.itemSelectionStore.addSelectedItems(roiIds);
  }

  public removeAllLabelsOnSelectedItems(): void {
    const selectedItems = this.itemSelectionStore.selectedItemsArray$;
    this.roiLabelingEffects.removeAllLabels(selectedItems.pipe(take(1)));
    this.clearSelection();
  }

  private isLoadMoreGated(filters: Filter[]): boolean {
    return filters.some(
      (f) =>
        ((f.field.id === 'changed' || f.field.id === 'unlabeled') && f.value === 'true') ||
        f.field.id === 'roiId'
    );
  }

  private resolveLabelIds(filters: Filter[], options: Record<string, Option>): string[] {
    const optionsByName = Object.values(options);
    return filters
      .filter((f) => f.field.id === 'label')
      .map((f) => optionsByName.find((option) => option.name === f.value)?.id)
      .filter((id): id is string => Boolean(id));
  }
}
